// New w-artifact custom element that loads page or artifact HTML in sandboxed iframe
// See docs in iframe-proxy.md

import type { PageApi } from '@shared/api/pages/types'
import type { Content, Endpoints } from '@shared/api/web-components/make-artifact-api'
import type { AppConfigOverrides } from '@shared/app-config'
import type { Artifact } from '@shared/types/artifacts'
import type { ProxyConfig } from '@shared/wippy-package/wippy-package'
import { loadESMComponentByPackageJSON, loadWebPageSourceByPackageJson } from '@shared/api/web-components'
import {
  defaultArtifactEndpoint,
  defaultPageEndpoints,
  makeArtifactApi,
} from '@shared/api/web-components/make-artifact-api'
import { ArtifactStatus, ArtifactType } from '@shared/types/artifacts'
import { isWippyPackageComponent, isWippyPackageESMModule, isWippyPackageWebPage } from '@shared/wippy-package/wippy-package'
import '@/proxy/components/w-iframe'

// Kick off the heavy markdown module (shiki + Oniguruma WASM, ~9 MB) as soon
// as this module is loaded so it's ready by the time an artifact actually
// needs it. Loaded via external import map entry (see web-components/constants.ts)
// so its code does not inline into proxy.js; UMD output can't code-split.
// The import map is injected before proxy.js in the srcdoc (ensured by
// processWebPage) so the bare specifier resolves via the document's import map.
const parseMarkdownIframePromise = import('@wippy-fe/markdown-iframe').then(m => m.parseMarkdownIframe)

// Surface loading failures through the proxy logger once the instance exists.
// Attaching a handler also marks the original promise as handled, so awaiting
// it later in renderMarkdownIframe still throws but no unhandled rejection.
parseMarkdownIframePromise.catch((err) => {
  window.getWippyApi?.()
    .then(inst => inst.logger.captureException(err, {
      source: '@wippy-fe/markdown-iframe',
      component: 'w-artifact',
    }))
    .catch(() => {
      // Logger itself unavailable; fall back to console so the error is not lost.
      console.error('[w-artifact] Failed to load @wippy-fe/markdown-iframe:', err)
    })
})

// Helpers to convert attr value to boolean
function attrBoolean(val: string | null | undefined): boolean {
  if (val === null || val === undefined)
    return false
  if (val === '')
    return true
  return val === 'true'
}

function generateStyle(tagName: string, fillContainer = false) {
  const heightRule = fillContainer ? 'height:100%;' : ''
  return `
      :host { display:block; ${heightRule} }
      .loader { display:flex; align-items:center; justify-content:center; padding:1rem; font-size:14px; color:#666; ${heightRule} }
      .error { color: red; padding:1rem; }
      ${tagName} { display:block; width:100%; ${heightRule} border:0; }
    `
}

const mainProps = ['id', 'type', 'auto-height', 'url', 'sub-path', 'nav-owner']

class WArtifactElement extends HTMLElement {
  static get observedAttributes() {
    return [...mainProps]
  }

  /** element attributes */
  private artifactId: string | null = null
  private artifactType: 'page' | 'artifact' = 'artifact'
  /** Human label from the meta API fetch — used for the iframe title. */
  private resourceLabel: string | null = null
  private autoHeight = false
  private customUrl: string | null = null
  private subPath: string | null = null
  private navOwner = false

  /** internals */
  private shadow: ShadowRoot
  private element: HTMLElement | null = null
  private loadingEl!: HTMLElement
  private errorEl!: HTMLElement
  private styleEl!: HTMLStyleElement
  private otherProps = new Map<string, string | null>()
  private endpoints: Endpoints = defaultArtifactEndpoint
  private observer: MutationObserver | null = null
  private loading = false

  constructor() {
    super()
    this.shadow = this.attachShadow({ mode: 'open' })
    // Basic styles
    this.styleEl = document.createElement('style')
    this.styleEl.textContent = generateStyle('iframe', !this.autoHeight)
    this.shadow.appendChild(this.styleEl)

    // Loading placeholder
    this.loadingEl = document.createElement('div')
    this.loadingEl.className = 'loader'
    this.loadingEl.textContent = 'Loading...'
    this.loadingEl.setAttribute('part', 'loader')
    this.shadow.appendChild(this.loadingEl)

    // Error placeholder
    this.errorEl = document.createElement('div')
    this.errorEl.className = 'error'
    this.errorEl.style.display = 'none'
    this.errorEl.setAttribute('part', 'error')
    this.shadow.appendChild(this.errorEl)
  }

  connectedCallback() {
    this.updateInternalState()
    this.load()
  }

  disconnectedCallback() {
    this.cleanup()
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (!this.isConnected)
      return
    // Nav-owner: once loading/loaded, never reload. Only forward sub-path changes.
    if (this.navOwner && (this.element || this.loading)) {
      if (name === 'sub-path' && this.element && oldValue !== null && newValue !== oldValue) {
        this.subPath = newValue
        this.element.setAttribute('sub-path', newValue || '')
      }
      return
    }
    this.updateInternalState()
    this.load()
  }

  /** read attributes into fields */
  private updateInternalState() {
    this.artifactId = this.getAttribute('id')
    const typeAttr = this.getAttribute('type')
    this.artifactType = (typeAttr === 'page' ? 'page' : 'artifact')
    this.autoHeight = attrBoolean(this.getAttribute('auto-height'))
    this.customUrl = this.getAttribute('url')
    this.subPath = this.getAttribute('sub-path')
    this.navOwner = this.hasAttribute('nav-owner')
    const existingProps = this.getAttributeNames()
    const prevProps = Object.keys(this.otherProps)

    // Remove previous props
    prevProps.forEach((prop) => {
      if (!existingProps.includes(prop)) {
        this.otherProps.delete(prop)
      }
    })
    // Update current props
    existingProps.forEach((prop) => {
      if (mainProps.includes(prop))
        return // skip main props
      const value = this.getAttribute(prop)
      this.otherProps.set(prop, value)
    })

    const endPoints = this.artifactType === 'page'
      ? { ...defaultPageEndpoints }
      : { ...defaultArtifactEndpoint }
    if (this.customUrl) {
      endPoints.content = () => this.customUrl!
    }
    this.endpoints = endPoints
  }

  private cleanup() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  private async loadMeta(): Promise<Artifact> {
    if (this.customUrl) {
      return {
        uuid: this.customUrl,
        title: this.customUrl,
        content_type: 'text/html',
        content_version: '',
        type: ArtifactType.INLINE_INTERACTIVE,
        status: ArtifactStatus.IDLE,
      }
    }
    const { api } = await window.getWippyApi()
    if (this.artifactType === 'page') {
      const response = await makeArtifactApi<PageApi.Page>(api, defaultPageEndpoints).getMeta(this.artifactId || '')
      return {
        uuid: response.id,
        title: response.title || 'Page',
        content_type: response.content_type || 'text/html',
        content_version: response.content_version || '',
        type: ArtifactType.INLINE_INTERACTIVE,
        status: ArtifactStatus.IDLE,
      }
    }
    else {
      const response = await makeArtifactApi<Artifact>(api, defaultArtifactEndpoint).getMeta(this.artifactId || '')
      return {
        ...response,
        type: ArtifactType.INLINE_INTERACTIVE,
        status: response.status || ArtifactStatus.IDLE,
      }
    }
  }

  /** fetch html content and render iframe */
  private async load() {
    // guard
    if (!this.customUrl && !this.artifactId)
      return

    // Remove previous stuff
    this.loading = true
    this.cleanup()
    if (this.element) {
      this.shadow.removeChild(this.element)
      this.element = null
    }

    // Show loading
    this.setStatus('loading')
    this.loadingEl.style.display = 'flex'
    this.errorEl.style.display = 'none'
    this.dispatchEvent(new CustomEvent('loading'))

    try {
      const [meta, content] = await Promise.all([this.loadMeta(), this.loadContent()])
      await this.render(meta, content)

      this.setStatus('ready')
      this.loadingEl.style.display = 'none'
      this.dispatchEvent(new CustomEvent('load'))
    }
    catch (err) {
      const message = (err as Error).message || 'Failed to load'
      this.setStatus('error')
      this.errorEl.textContent = message
      this.loadingEl.style.display = 'none'
      this.errorEl.style.display = 'block'
      this.dispatchEvent(new CustomEvent('error', { detail: err }))
    }

    this.loading = false
    this.setupAttributeObserver()
  }

  private async loadContent() {
    const { api } = await window.getWippyApi()
    return await makeArtifactApi(api, this.endpoints).getContent(this.artifactId || '')
  }

  private async render(meta: Artifact, content: Content) {
    // Capture the API-provided label for the iframe title (see renderIframe).
    this.resourceLabel = meta.title || null
    const contentType = content.contentType || meta.content_type || 'text/html'
    if (contentType === 'text/markdown')
      await this.renderMarkdownIframe(content)
    else if (contentType === 'application/json')
      await this.renderComponent(content)
    else
      await this.renderHtmlIframe(content)
  }

  private async renderComponent(content: Content) {
    const { api } = await window.getWippyApi()
    if (typeof content.data !== 'object') {
      throw new TypeError(`Invalid content type for component: expected object, got ${typeof content.data}`)
    }
    const packageJson = content.data
    if (isWippyPackageComponent(packageJson)) {
      if (packageJson.props) {
        for (const [key, value] of Object.entries(packageJson.props))
          this.otherProps.set(key, typeof value === 'string' ? value : JSON.stringify(value))
      }
      await this.renderDirectTag(packageJson.wippy.tagName)
    }
    else if (isWippyPackageESMModule(packageJson)) {
      if (packageJson.props) {
        for (const [key, value] of Object.entries(packageJson.props))
          this.otherProps.set(key, typeof value === 'string' ? value : JSON.stringify(value))
      }
      const tagName = await loadESMComponentByPackageJSON({ uuid: this.artifactId || '', packageJson }, api)
      this.renderTag(tagName)
    }
    else if (isWippyPackageWebPage(packageJson)) {
      const source = await loadWebPageSourceByPackageJson({ uuid: this.artifactId || '', packageJson }, api)
      await this.renderIframe(source.content, {
        baseUrl: source.baseUrl,
        proxyConfig: source.proxyConfig,
        configOverrides: source.configOverrides,
      })
    }
  }

  private async renderMarkdownIframe(content: Content) {
    if (typeof content.data !== 'string') {
      throw new TypeError(`Invalid content type for Markdown: expected string, got ${typeof content.data}`)
    }
    const parseMarkdownIframe = await parseMarkdownIframePromise
    const html = await parseMarkdownIframe(content.data)
    await this.renderIframe(html)
  }

  private async renderHtmlIframe(content: Content) {
    if (typeof content.data !== 'string') {
      throw new TypeError(`Invalid content type for HTML: expected string, got ${typeof content.data}`)
    }
    await this.renderIframe(content.data)
  }

  private renderIframe(html: string, options: { baseUrl?: string, proxyConfig?: ProxyConfig, configOverrides?: AppConfigOverrides } = {}) {
    this.styleEl.textContent = generateStyle('iframe', !this.autoHeight)
    const iframe = document.createElement('w-iframe') as HTMLElement & {
      srcdoc: string
      proxyConfig?: unknown
      configOverrides?: unknown
    }
    iframe.setAttribute('resource-id', this.artifactId || this.customUrl || '')
    iframe.setAttribute('resource-type', this.artifactType)
    if (this.resourceLabel)
      iframe.setAttribute('resource-label', this.resourceLabel)
    iframe.setAttribute('part', 'frame')
    iframe.setAttribute('exportparts', 'loader,error,frame')
    if (this.autoHeight)
      iframe.setAttribute('auto-height', '')
    if (this.subPath !== null)
      iframe.setAttribute('sub-path', this.subPath)
    if (this.navOwner)
      iframe.setAttribute('nav-owner', '')
    if (options.baseUrl)
      iframe.setAttribute('base-url', options.baseUrl)
    iframe.proxyConfig = options.proxyConfig
    iframe.configOverrides = options.configOverrides
    iframe.srcdoc = html

    const ready = new Promise<void>((resolve, reject) => {
      iframe.addEventListener('load', () => resolve(), { once: true })
      iframe.addEventListener('error', event => reject((event as unknown as CustomEvent).detail || new Error('Failed to load iframe')), { once: true })
    })

    this.shadow.appendChild(iframe)
    this.element = iframe

    return ready
  }

  private renderTag(tagName: string) {
    this.styleEl.textContent = generateStyle(tagName, !this.autoHeight)
    const childEl = document.createElement(tagName)
    this.otherProps.forEach((value, key) => {
      childEl.setAttribute(key, value || '')
    })
    childEl.setAttribute('part', 'frame')

    this.shadow.appendChild(childEl)
    this.element = childEl
  }

  private updateTagProps() {
    if (!this.element)
      return

    // Only sync props for non-iframe elements (web component tags).
    // Iframe elements have their attributes (srcdoc, sandbox, style) set by
    // renderIframe; stripping them here would destroy the iframe content.
    if (!(this.element instanceof HTMLIFrameElement) && this.element.tagName !== 'W-IFRAME') {
      const existingAttributes = this.element.getAttributeNames()
      for (const attr of existingAttributes) {
        if (attr !== 'part' && !this.otherProps.has(attr))
          this.element?.removeAttribute(attr)
      }
    }

    // Set current props if they are different from old one
    this.otherProps.forEach((value, key) => {
      if (this.element?.getAttribute(key) !== value) {
        if (value === null || value === undefined) {
          this.element?.removeAttribute(key)
        }
        else {
          this.element?.setAttribute(key, value)
        }
      }
    })
  }

  private async renderDirectTag(tagName: string) {
    if (!customElements.get(tagName)) {
      const timeout = 10_000
      let timer: ReturnType<typeof setTimeout>
      await Promise.race([
        customElements.whenDefined(tagName),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error(`<${tagName}> not defined within ${timeout}ms`)), timeout)
        }),
      ]).finally(() => clearTimeout(timer))
    }
    this.renderTag(tagName)
  }

  private setupAttributeObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          this.updateInternalState()
          this.updateTagProps()
        }
      })
    })
    this.observer.observe(this, { attributes: true })
  }

  private setStatus(status: 'loading' | 'ready' | 'error') {
    this.setAttribute('status', status)
  }
}

// Register element only once
if (!customElements.get('w-artifact')) {
  customElements.define('w-artifact', WArtifactElement)
}
